import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceType } from 'types/invoices';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useInvoice } from './useInvoice';

const NO_EMPLOYEE_NAMES: string[] = [];

type InvoiceDetailsActions = {
    hasUninvoicedTimecards: boolean;
    hasIgnoredCurrentMonthTimecards: boolean;
    hasUninvoicedExpenses: boolean;
    isDetailsReadOnly: boolean;
    hasDetailAlerts: boolean;
    isTimecardEmployeeMissingRole: boolean;
    isTimecardMissingHourlyRate: boolean;
    missingHourlyRateEmployeeNames: string[];
};

export const useInvoiceDetails = (invoice: InvoiceData): InvoiceDetailsActions => {
    const { isFinalized } = useInvoice(invoice);

    const ignoreCurrentMonthTimecards = useIsFeatureEnabled(FeatureFlag.NEW_TIMECARDS_WARNING_IGNORE_CURRENT_MONTH);

    const hasUninvoicedTimecards =
        invoice.hasUninvoicedTimecardsPrevMonth ||
        (invoice.hasUninvoicedTimecardsThisMonth && !ignoreCurrentMonthTimecards);
    const hasIgnoredCurrentMonthTimecards =
        ignoreCurrentMonthTimecards &&
        invoice.hasUninvoicedTimecardsThisMonth &&
        !invoice.hasUninvoicedTimecardsPrevMonth;
    const hasUninvoicedExpenses = invoice.hasUninvoicedExpensesThisMonth || invoice.hasUninvoicedExpensesPrevMonth;

    const isDetailsReadOnly = invoice.invoiceTypeCode === InvoiceType.CREDIT || (invoice.isSplit ?? false);

    const isTimecardEmployeeMissingRole = invoice.containsMissingJobRole;
    const isTimecardMissingHourlyRate = invoice.containsMissingHourlyRate ?? false;
    const missingHourlyRateEmployeeNames = invoice.missingHourlyRateEmployeeNames ?? NO_EMPLOYEE_NAMES;

    const hasDetailAlerts =
        (hasUninvoicedTimecards ||
            hasUninvoicedExpenses ||
            invoice.hasMatterHourlyPriceChanged ||
            isTimecardEmployeeMissingRole ||
            isTimecardMissingHourlyRate) &&
        invoice.invoiceTypeCode === InvoiceType.DEBIT &&
        !invoice.isSplit &&
        !isFinalized;

    return {
        hasUninvoicedTimecards,
        hasIgnoredCurrentMonthTimecards,
        hasUninvoicedExpenses,
        isDetailsReadOnly,
        hasDetailAlerts,
        isTimecardEmployeeMissingRole,
        isTimecardMissingHourlyRate,
        missingHourlyRateEmployeeNames,
    };
};
